import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { supabase } from './supabaseClient'

interface AuthContextShape {
  user: User | null
  session: Session | null
  loading: boolean
  signInWithGoogle: () => Promise<void>
  signOut: () => Promise<void>
  signInWithEmail: (email: string, password: string) => Promise<{ error?: string }>
  signUpWithEmail: (payload: SignUpPayload) => Promise<{ error?: string; needsConfirmation?: boolean }>
}

const AuthContext = createContext<AuthContextShape | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session ?? null)
      setLoading(false)
    })

    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s)
      // Refresh cached plan for gating (store exact plan: 'free'|'open'|'pro')
      ;(async () => {
        try {
          const uid = s?.user?.id
          if (!uid) return
          const { data, error } = await supabase.from('profiles').select('plan, pro_until').eq('user_id', uid).maybeSingle()
          if (!error && data) {
            const rawPlan = ((data as any).plan || 'free').toString().toLowerCase()
            const proUntil = (data as any).pro_until ? new Date((data as any).pro_until) : null
            const effectivePlan = (rawPlan === 'pro' || (proUntil && proUntil > new Date())) ? 'pro' : (rawPlan === 'open' ? 'open' : 'free')
            try { localStorage.setItem('pt_plan', effectivePlan) } catch { /* ignore */ }
          }
        } catch { /* ignore */ }
      })()
      // When a new session starts, ensure profile exists from user metadata (if provided during sign-up)
      const meta = s?.user?.user_metadata as any
      if (s?.user && meta && (meta.first_name || meta.last_name)) {
        upsertProfile({
          user_id: s.user.id,
          first_name: meta.first_name || '',
          last_name: meta.last_name || '',
          nickname: meta.nickname,
          country_code: meta.country_code,
          region: meta.region,
          postal_code: meta.postal_code,
          age: typeof meta.age === 'number' ? meta.age : undefined,
          gender: (meta.gender === 'M' || meta.gender === 'F' || meta.gender === 'N') ? meta.gender : 'N'
        })
      }
    })
    return () => {
      sub.subscription.unsubscribe()
    }
  }, [])

  const value = useMemo<AuthContextShape>(() => ({
    user: session?.user ?? null,
    session,
    loading,
    signInWithGoogle: async () => {
      setLoading(true)
      const redirectTo = window.location.origin
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo }
      })
      if (error) console.error('[auth] google sign-in failed', error)
      setLoading(false)
    },
    signOut: async () => {
      try {
        await supabase.auth.signOut()
      } finally {
        // Fallback: clear cached tokens and flags if signOut fails silently
        try {
          localStorage.removeItem('sb-access-token')
          localStorage.removeItem('sb-refresh-token')
          localStorage.removeItem('pt_pro')
          localStorage.removeItem('pt_plan')
        } catch { /* ignore */ }
        // Force reload to clear in-memory state
        try { window.location.reload() } catch { /* ignore */ }
      }
    },
    signInWithEmail: async (email: string, password: string) => {
      const { error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) {
        console.error('[auth] email sign-in failed', error)
        return { error: error.message }
      }
      return {}
    },
    signUpWithEmail: async (payload: SignUpPayload) => {
      const { email, password, first_name, last_name, nickname, country_code, region, postal_code, gender, age } = payload
      const user_metadata = { first_name, last_name, nickname, country_code, region, postal_code, gender, age }
      const { data, error } = await supabase.auth.signUp({ email, password, options: { data: user_metadata } })
      if (error) {
        console.error('[auth] email sign-up failed', error)
        return { error: error.message }
      }
      // If session exists (no email confirmation required), upsert profile now
      const uid = data.session?.user?.id ?? data.user?.id
      if (uid && data.session) {
        await upsertProfile({ user_id: uid, first_name, last_name, nickname, country_code, region, postal_code, gender, age })
      }
      return { needsConfirmation: !data.session }
    }
  }), [session, loading])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

// Types and helpers
export type Gender = 'M' | 'F' | 'N'
export type SignUpPayload = {
  email: string
  password: string
  first_name: string
  last_name: string
  nickname?: string
  country_code: string
  region: string
  postal_code: string
  age?: number
  gender: Gender
}

async function upsertProfile(profile: {
  user_id: string
  first_name: string
  last_name: string
  nickname?: string
  country_code: string
  region: string
  postal_code: string
  age?: number
  gender: Gender
}) {
  const { error } = await supabase.from('profiles').upsert(profile, { onConflict: 'user_id' })
  if (error) console.warn('[profiles] upsert failed', error)
}
